import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/Button';
import { Input, Textarea } from '../ui/Input';
import { Card, CardBody } from '../ui/Card';
import { SectionCardHeader } from '../ui/SectionCard';
import NoteTemplatesSection from './NoteTemplatesSection';
import { Alert } from '../ui/Alert';
import { Dialog, DialogHeader, DialogBody, DialogFooter } from '../ui/Dialog';
import { useConfirm, useToast } from '../ui';
import { settingsAPI } from '../../services/api';
import {
  PencilSquareIcon,
  TrashIcon,
  DocumentDuplicateIcon,
  ClipboardDocumentListIcon,
} from '@heroicons/react/24/outline';
import PageHeader from '../ui/PageHeader';
import { toDisplayString } from '../../utils/displayString';
import { useDealerWorkspace } from '../admin/DealerWorkspaceContext';

/**
 * QuoteTemplatesPage — Settings › Quote Templates
 *
 * Iter18 §2.6 rework:
 *   • No in-Settings creation. Templates are created from the Quote Builder only
 *     ("Save as template"). The "+ New Template" CTA is removed.
 *   • Light edit (in-page modal): name + description + read-only line-item view.
 *   • Full edit: opens the Quote Builder with ?editing_template_id=<id>. The
 *     builder shows an "Editing Template: {name}" chip and saves back to the
 *     template on submit, then returns to /settings/quote-templates.
 *   • Duplicate + Delete row actions preserved.
 */
export default function QuoteTemplatesPage() {
  const confirm = useConfirm();
  const toast = useToast();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightEdit, setLightEdit] = useState(null); // template object or null
  const [previewTemplate, setPreviewTemplate] = useState(null); // opened on card click

  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const res = await settingsAPI.getQuoteTemplates();
      setTemplates(res || []);
    } catch (err) {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const handleDuplicate = async (template) => {
    try {
      await settingsAPI.createQuoteTemplate({
        ...template,
        name: `${toDisplayString(template.name, 'Template')} (Copy)`,
        id: undefined,
      });
      toast.success('Template duplicated');
      await fetchTemplates();
    } catch (err) {
      toast.error('Failed to duplicate template');
    }
  };

  const handleDelete = async (template) => {
    const ok = await confirm({
      title: 'Delete template?',
      message: `Delete template "${toDisplayString(template.name)}"? This cannot be undone.`,
      confirmLabel: 'Delete',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await settingsAPI.deleteQuoteTemplate(template.id);
      toast.success('Template deleted');
      await fetchTemplates();
    } catch (err) {
      toast.error('Failed to delete template');
    }
  };

  const handleLightSave = async (form) => {
    try {
      await settingsAPI.updateQuoteTemplate(lightEdit.id, {
        ...lightEdit,
        name: form.name,
        description: form.description,
      });
      toast.success('Template updated');
      setLightEdit(null);
      await fetchTemplates();
    } catch (err) {
      throw new Error(err.message || 'Failed to update template');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="space-y-3 w-full max-w-sm">
          <div className="h-8 rounded animate-pulse" style={{ backgroundColor: 'var(--neutral-100)' }} />
          <div className="h-32 rounded animate-pulse" style={{ backgroundColor: 'var(--neutral-100)' }} />
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Templates"
        subtitle="Quote templates and note templates for reuse across your quotes."
      />

      {/* R20-17 / B4: Section 1 — Quote Templates. Card + SectionCardHeader. */}
      <Card>
        <SectionCardHeader
          title="Quote Templates"
          description="Pre-filled line-item sets for common quote types. Created from the Quote Builder via Save as template."
          icon={ClipboardDocumentListIcon}
        />
        <CardBody>
          {templates.length === 0 ? (
            <div className="py-8 text-center">
              <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
                No quote templates yet.
              </p>
              <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
                Build a quote at <span className="font-medium">Quotes › New</span> and use{' '}
                <span className="font-medium">Save as template</span> to create your first one.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {templates.map((template) => (
                <TemplateCard
                  key={template.id}
                  template={template}
                  onOpenPreview={() => setPreviewTemplate(template)}
                  onLightEdit={() => setLightEdit(template)}
                  onDuplicate={() => handleDuplicate(template)}
                  onDelete={() => handleDelete(template)}
                />
              ))}
            </div>
          )}
        </CardBody>
      </Card>

      {/* R20-18: Section 2 — Note Templates (beta minimum per spec § B1a). */}
      <NoteTemplatesSection />

      <LightEditModal
        open={!!lightEdit}
        template={lightEdit}
        onClose={() => setLightEdit(null)}
        onSave={handleLightSave}
      />

      <TemplatePreviewModal
        open={!!previewTemplate}
        template={previewTemplate}
        onClose={() => setPreviewTemplate(null)}
      />
    </div>
  );
}

// Iter18 R2 §2.7: Full-detail preview modal, opened on card body click.
// Shows every line item + all metadata. The "Edit in Quote Builder" CTA is
// the entry point for the (backlog) full-edit round-trip; today it links to
// /quotes/add?editing_template_id=X and shows a "Preview mode" hint until the
// Builder's polish pass wires up the round-trip.
function TemplatePreviewModal({ open, template, onClose }) {
  const workspace = useDealerWorkspace(); // IDI-63
  if (!template) return null;
  const fullEditHref = `/quotes/add?editing_template_id=${encodeURIComponent(template.id)}`;
  return (
    <Dialog open={open} onClose={onClose} size="lg">
      <DialogHeader onClose={onClose}>{toDisplayString(template.name, '(unnamed)')}</DialogHeader>
      <DialogBody>
        {(() => {
          const desc = toDisplayString(template.description);
          return desc ? (
            <p className="text-sm mb-4" style={{ color: 'var(--color-text-muted)' }}>
              {desc}
            </p>
          ) : null;
        })()}

        <div className="flex flex-wrap gap-2 mb-4">
          {template.property_type && (
            <span
              className="inline-flex items-center px-2 py-0.5 text-xs font-medium uppercase tracking-wide"
              style={{
                backgroundColor: 'var(--neutral-100)',
                color: 'var(--color-text-muted)',
                borderRadius: 'var(--radius-sm)',
              }}
            >
              {template.property_type}
            </span>
          )}
          {template.updated_at && (
            <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
              Last updated {new Date(template.updated_at).toLocaleDateString()}
            </span>
          )}
        </div>

        {template.line_items?.length > 0 ? (
          <div
            className="overflow-hidden mb-4"
            style={{
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-md)',
            }}
          >
            <table className="data-table">
              <thead>
                <tr>
                  <th>Item</th>
                  <th style={{ textAlign: 'right' }}>Qty</th>
                  <th style={{ textAlign: 'right' }}>Unit price</th>
                  <th style={{ textAlign: 'right' }}>Line total</th>
                </tr>
              </thead>
              <tbody>
                {template.line_items.map((item, idx) => {
                  const qty = Number(item.quantity ?? 0);
                  const unit = Number(item.unit_price ?? 0);
                  const lineTotal = qty * unit;
                  return (
                    <tr key={idx}>
                      <td>{toDisplayString(item.description, toDisplayString(item.name, '—'))}</td>
                      <td style={{ textAlign: 'right' }}>{item.quantity ? `×${item.quantity}` : '—'}</td>
                      <td style={{ textAlign: 'right' }}>{item.unit_price ? `$${unit.toFixed(2)}` : '—'}</td>
                      <td style={{ textAlign: 'right' }}>{lineTotal ? `$${lineTotal.toFixed(2)}` : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-center py-8" style={{ color: 'var(--color-text-muted)' }}>
            This template has no line items yet.
          </p>
        )}

        {!workspace && (
          <Alert variant="info">
            <strong>Preview mode.</strong> Full line-item editing lives in the Quote Builder.
            Wiring the builder to load this template on open is planned for the Quote Builder's
            polish pass — for now, clicking Edit navigates but the builder may not yet know
            to hydrate the template.
          </Alert>
        )}
      </DialogBody>
      <DialogFooter>
        <Button variant="secondary" onClick={onClose}>Close</Button>
        {!workspace && (
          <Button
            onClick={() => { window.location.href = fullEditHref; }}
          >
            Edit in Quote Builder
          </Button>
        )}
      </DialogFooter>
    </Dialog>
  );
}

// Iter18 R2 §2.7: card body is clickable → opens TemplatePreviewModal with full detail.
// Icon action buttons (edit / duplicate / delete) call stopPropagation to avoid
// double-firing when the operator clicks them.
function TemplateCard({ template, onOpenPreview, onLightEdit, onDuplicate, onDelete }) {
  const stopAnd = (fn) => (e) => { e.stopPropagation(); fn?.(); };

  return (
    <Card
      className="cursor-pointer"
      onClick={onOpenPreview}
    >
      <div className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold" style={{ color: 'var(--color-text-default)' }}>
              {toDisplayString(template.name, '(unnamed)')}
            </h3>
            {(() => {
              const desc = toDisplayString(template.description);
              return desc ? (
                <p className="text-xs mt-0.5" style={{ color: 'var(--color-text-muted)' }}>
                  {desc}
                </p>
              ) : null;
            })()}
            {template.property_type && (
              <span
                className="inline-flex mt-1.5 items-center px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide"
                style={{
                  backgroundColor: 'var(--neutral-100)',
                  color: 'var(--color-text-muted)',
                  borderRadius: 'var(--radius-sm)',
                }}
              >
                {template.property_type}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              onClick={stopAnd(onLightEdit)}
              className="p-1.5 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100"
              aria-label={`Edit ${toDisplayString(template.name)} (light)`}
              title="Edit name & description"
            >
              <PencilSquareIcon className="h-4 w-4" />
            </button>
            <button
              onClick={stopAnd(onDuplicate)}
              className="p-1.5 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100"
              aria-label={`Duplicate ${toDisplayString(template.name)}`}
              title="Duplicate"
            >
              <DocumentDuplicateIcon className="h-4 w-4" />
            </button>
            <button
              onClick={stopAnd(onDelete)}
              className="p-1.5 rounded text-gray-400 hover:text-red-600 hover:bg-red-50"
              aria-label={`Delete ${toDisplayString(template.name)}`}
              title="Delete"
            >
              <TrashIcon className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Read-only line-item preview */}
        {template.line_items?.length > 0 && (
          <div className="mt-3 space-y-1 border-t border-gray-100 pt-3">
            {template.line_items.slice(0, 3).map((item, idx) => (
              <div key={idx} className="text-xs flex justify-between" style={{ color: 'var(--color-text-muted)' }}>
                <span className="truncate">{toDisplayString(item.description, toDisplayString(item.name))}</span>
                {item.quantity && <span className="ml-2">×{item.quantity}</span>}
              </div>
            ))}
            {template.line_items.length > 3 && (
              <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                +{template.line_items.length - 3} more items
              </p>
            )}
          </div>
        )}

        {/* Iter18 R2 Round 4 (B): Removed the "Edit line items in Quote Builder"
            inline link — the card-click flow opens TemplatePreviewModal which
            already exposes an "Edit in Quote Builder" primary CTA, so this
            link was redundant and visually noisy. */}
      </div>
    </Card>
  );
}

function LightEditModal({ open, template, onClose, onSave }) {
  const workspace = useDealerWorkspace(); // IDI-63
  const [form, setForm] = useState({ name: '', description: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && template) {
      // PR-11: coerce on open so opening a corrupted-shape row shows the
      // unwrapped strings, and saving writes them back clean (self-serve heal).
      setForm({ name: toDisplayString(template.name), description: toDisplayString(template.description) });
      setError(null);
    }
  }, [open, template]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setError('Template name is required');
      return;
    }
    try {
      setSaving(true);
      await onSave(form);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!template) return null;

  return (
    <Dialog open={open} onClose={onClose} size="md">
      <DialogHeader onClose={onClose}>Edit template</DialogHeader>
      <form onSubmit={handleSubmit}>
        <DialogBody>
          {error && <Alert variant="error" className="mb-3">{error}</Alert>}

          <div className="space-y-4">
            <div>
              <label htmlFor="light-name" className="block text-sm font-medium text-gray-700 mb-1">
                Name <span className="text-red-500">*</span>
              </label>
              <Input
                id="light-name"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Template name"
                required
                autoFocus
              />
            </div>
            <div>
              <label htmlFor="light-desc" className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <Textarea
                id="light-desc"
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                placeholder="Optional short description of when to use this template."
                rows={3}
              />
            </div>

            {/* Read-only line-item preview */}
            {template.line_items?.length > 0 && (
              <div>
                <p className="text-sm font-medium text-gray-700 mb-1">Line items (read-only)</p>
                <div className="border border-[color:var(--color-border)] rounded-md divide-y divide-gray-100 max-h-40 overflow-y-auto">
                  {template.line_items.map((item, idx) => (
                    <div key={idx} className="px-3 py-1.5 text-xs flex justify-between" style={{ color: 'var(--color-text-muted)' }}>
                      <span className="truncate">{toDisplayString(item.description, toDisplayString(item.name))}</span>
                      {item.quantity && <span className="ml-2">×{item.quantity}</span>}
                    </div>
                  ))}
                </div>
                {/* IDI-63: the Quote Builder is a dealer's quote, refused to a
                    super-admin in a dealer workspace, so no link there. */}
                {!workspace && (
                <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
                  To change line items, use{' '}
                  <a
                    href={`/quotes/add?editing_template_id=${encodeURIComponent(template.id)}`}
                    className="font-medium underline"
                    style={{ color: 'var(--color-action-primary)' }}
                  >
                    Edit in Quote Builder
                  </a>.
                </p>
                )}
              </div>
            )}
          </div>
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={saving}>
            {saving ? 'Saving…' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}
